import { useTranslation } from 'react-i18next';
import { Position, TemplateProduct } from '@/shared/types/prefill-template-data.ts';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react';
import { Menu } from '@headlessui/react';
import { PlusIcon } from '@schuettflix/icons-react';
import CategoryIcon from '@/shared/components/AddProductButton/components/CategoryIcon.tsx';
import { useTemplate } from '@/clients/template/useTemplate';
import { getCompatibleProducts } from '@/shared/utils/getCompatibleProducts';
import { PRODUCT_NAME_LABEL } from '@/phrase/ProductNameLabels';

interface AddProductButtonProps {
    templateId: string;
    onAddProduct: (product: TemplateProduct) => void;
}

export function AddProductButton({ templateId, onAddProduct }: AddProductButtonProps) {
    const { t } = useTranslation();
    const { data: template } = useTemplate(templateId);
    const compatibleProducts = template
        ? getCompatibleProducts(
              template.type,
              template.preSelectedProducts.map(({ type }) => type)
          )
        : [];

    const {
        // floating-ui returns these as stable `useCallback` setters that never touch `this`,
        // but types them as methods, which trips `unbound-method`.
        // eslint-disable-next-line @typescript-eslint/unbound-method
        refs: { setReference, setFloating },
        floatingStyles,
    } = useFloating({
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [flip(), offset(8)],
        whileElementsMounted: autoUpdate,
    });

    return (
        <Menu as="span">
            <Menu.Button
                name="add-product-button"
                className="flex shrink cursor-pointer items-center gap-2 py-2"
                ref={setReference}
            >
                <PlusIcon size="xs" />
                <p className="font-copy-md-strong">{t('product.addPosition.btn')}</p>
            </Menu.Button>
            <Menu.Items
                as="div"
                style={floatingStyles}
                ref={setFloating}
                className="card shadow-high z-10 w-[432px] rounded p-0"
            >
                <Menu.Item as="div" className="font-copy-md-strong p-4">
                    {t('product.addPosition.label')}
                </Menu.Item>
                {compatibleProducts.map((position: Position) => (
                    <Menu.Item
                        key={position.product.type}
                        as="div"
                        onClick={() => onAddProduct(position.product)}
                        className="hover:bg-surface-disabled flex w-full cursor-pointer justify-between gap-2 p-4"
                    >
                        <span className="font-copy-md">
                            {t(position.productName as (typeof PRODUCT_NAME_LABEL)[keyof typeof PRODUCT_NAME_LABEL])}
                        </span>
                        <div className="shrink-0">
                            <CategoryIcon productCategory={position.icon} />
                        </div>
                    </Menu.Item>
                ))}
            </Menu.Items>
        </Menu>
    );
}
